import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { FadeUp, Label } from "./Primitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, {
        email,
        source: "newsletter",
      });
      setStatus("done");
      toast.success(res.data.message || "Welcome to the inner circle.");
      setEmail("");
      setTimeout(() => setStatus("idle"), 3500);
    } catch (err) {
      setStatus("idle");
      const status = err?.response?.status;
      const msg =
        status === 422
          ? "Please enter a valid email address."
          : "Something went wrong. Please try again.";
      toast.error(msg);
    }
  };

  return (
    <section
      id="newsletter"
      data-testid="newsletter"
      className="relative border-t border-white/8 bg-[#080808] overflow-hidden"
    >
      <div className="crimson-glow absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[700px] rounded-full opacity-40" />

      <div className="relative mx-auto max-w-[1100px] px-6 md:px-10 py-28 md:py-40 text-center">
        <FadeUp>
          <Label>Dispatches from the Dark</Label>
        </FadeUp>
        <FadeUp delay={0.05}>
          <h2 className="mt-6 font-display text-5xl md:text-8xl leading-[0.9] tracking-tight">
            Enter the <span className="text-crimson italic">Inner Circle.</span>
          </h2>
        </FadeUp>
        <FadeUp delay={0.1}>
          <p className="mx-auto mt-6 max-w-xl text-white/55 text-base md:text-lg leading-relaxed">
            One letter a week. New films, unreleased frameworks, and the
            psychology the algorithm won't show you.
          </p>
        </FadeUp>

        <FadeUp delay={0.15}>
          <form
            onSubmit={submit}
            data-testid="newsletter-form"
            className="mx-auto mt-12 flex max-w-xl items-center gap-4 border-b border-white/20 pb-2 focus-within:border-[var(--noir-crimson)] transition-colors"
          >
            <input
              data-testid="newsletter-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="flex-1 bg-transparent py-3 text-lg md:text-xl text-white placeholder:text-white/30 outline-none font-display tracking-wide"
              aria-label="Email address"
            />
            <motion.button
              data-testid="newsletter-submit"
              type="submit"
              disabled={status !== "idle"}
              whileTap={{ scale: 0.96 }}
              className="group flex items-center gap-2 px-6 py-3 text-white disabled:opacity-70"
              style={{ background: status === "done" ? "transparent" : "var(--noir-crimson)" }}
              aria-label="Subscribe"
            >
              {status === "loading" && <Loader2 size={18} className="animate-spin" />}
              {status === "done" && <Check size={18} className="text-crimson" />}
              {status === "idle" && (
                <>
                  <span className="font-mono-accent text-[11px] uppercase tracking-[0.2em]">
                    Subscribe
                  </span>
                  <ArrowRight
                    size={16}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </>
              )}
            </motion.button>
          </form>
        </FadeUp>
        <FadeUp delay={0.2}>
          <p className="mt-4 font-mono-accent text-[10px] uppercase tracking-[0.2em] text-white/30">
            No spam. Only signal.
          </p>
        </FadeUp>
      </div>
    </section>
  );
}
